/**
 * **Disposable fixture — Network Log catalog entries.**
 *
 * Authored because the `/available_integrations` catalog returns only `Cloud`
 * definitions today: the Network Log half of `connector_definition` has no rows
 * yet, so the add-catalog's Network Logs tab has nothing to reveal itself for.
 * These rows are that half, in the wire shape the endpoint already returns —
 * nothing about the tab, the rail or the card is gated on a flag, so seeding the
 * data is the whole change.
 *
 * **Delete condition:** the first real `Network Log` definition in the catalog
 * response. When one lands, this file and the `withNetworkLogCatalog()` call in
 * `features/integrations/api.ts` are deleted together.
 *
 * **These rows have no backend behind them, and the catalog is shared.** Two
 * consequences worth knowing before adding a seventh:
 * - A seeded card does **not** go to `/integrations/new/$id` like a Cloud one.
 *   That route asks the backend for a definition these ids do not have, and a
 *   Log Source has no connector to set up anyway — so a `Network Log` card opens
 *   the Add Log Source wizard instead (ticket 08), which reads its type back out
 *   of this catalog rather than from a by-id definition read.
 * - Every catalog consumer sees them, not only the add-catalog. The one that
 *   shows: `useCatalogLogSourceOptions` (`../../filter-options.ts`) offers every
 *   `Log Ingestion` catalog entry as a Log Source filter on the multi-tenant
 *   policies table, so these six appear there and match no row. The real
 *   definitions will do exactly the same, so it is the shared-catalog design
 *   (D12) showing rather than a fixture artefact — but on this branch the rows
 *   are fictional.
 *
 * **Not demo-world content** (ADR 0017): the demo build authors its own catalog
 * from the real contract (`src/__mocks__/world/data/integrations.ts`) and must
 * never import this.
 *
 * Ids are **UUIDs** because real definition ids are (see
 * `features/integrations/constants.ts`), and a definition id reaches the URL at
 * `/integrations/collectors/sources/new/$definitionId`. The three the wizard
 * branches on are exported by name so `register-form.ts` and
 * `source-instructions.ts` can key off an id rather than a display name.
 *
 * The set is deliberately small and vendor-plausible rather than exhaustive: two
 * display groups so the category rail has work to do, and the three source types
 * whose Register/Verify behaviour differs (Palo Alto: no extra install step;
 * NX Log: a Windows agent package; rsyslog: a config file).
 */
import type { AvailableIntegration } from '../../types'

/** Palo Alto PAN-OS — the wizard's default source type; no extra install step. */
export const PALO_ALTO_DEFINITION_ID = 'c2f8a41e-6b0d-4d27-9a3c-5e17b8d40f01'

/**
 * The two firewalls that need no per-type install block, and are not exported:
 * nothing outside this file branches on them — they exist so the type map below
 * and the rows themselves cannot spell the same id twice.
 */
const FORTINET_DEFINITION_ID = 'c2f8a41e-6b0d-4d27-9a3c-5e17b8d40f02'
const CISCO_ASA_DEFINITION_ID = 'c2f8a41e-6b0d-4d27-9a3c-5e17b8d40f03'

/** NX Log — Register offers a Windows agent package from the Help Center. */
export const NXLOG_DEFINITION_ID = 'c2f8a41e-6b0d-4d27-9a3c-5e17b8d40f04'

/** rsyslog — Register offers a config file carrying the destination IP/port. */
export const RSYSLOG_DEFINITION_ID = 'c2f8a41e-6b0d-4d27-9a3c-5e17b8d40f05'

/**
 * `capabilities` is `Log Ingestion` on every row and nothing else: a Log Source
 * ships logs to AIR® and cannot be acted back through, so `Response` would be a
 * claim the collector cannot honour. It is also what the card's capability
 * badges render, so an empty array would be a card with a blank third line.
 *
 * `event_types` is empty. The Cloud entries' event types are the per-connector
 * subscriptions their setup form offers; a Log Source receives whatever the
 * device sends to its port, so there is nothing to subscribe to.
 */
export const NETWORK_LOG_CATALOG_FIXTURES: Array<AvailableIntegration> = [
	{
		id: PALO_ALTO_DEFINITION_ID,
		vendor: 'Palo Alto Networks',
		product: 'PAN-OS',
		version: '1.0.0',
		name: 'Palo Alto Firewall',
		description:
			'Traffic, threat and URL-filtering logs from a PAN-OS firewall, forwarded to a Collector over syslog.',
		event_types: [],
		visible: true,
		capabilities: ['Log Ingestion'],
		category: 'Firewall / NGFW',
		integration_type: 'Network Log',
	},
	{
		id: FORTINET_DEFINITION_ID,
		vendor: 'Fortinet',
		product: 'FortiGate',
		version: '1.0.0',
		name: 'Fortinet FortiGate',
		description:
			'Firewall, IPS and web-filter logs from a FortiGate appliance, forwarded to a Collector over syslog.',
		event_types: [],
		visible: true,
		capabilities: ['Log Ingestion'],
		category: 'Firewall / NGFW',
		integration_type: 'Network Log',
	},
	{
		id: CISCO_ASA_DEFINITION_ID,
		vendor: 'Cisco',
		product: 'ASA',
		version: '1.0.0',
		name: 'Cisco ASA',
		description:
			'Connection, VPN and access-list logs from a Cisco ASA firewall, forwarded to a Collector over syslog.',
		event_types: [],
		visible: true,
		capabilities: ['Log Ingestion'],
		category: 'Firewall / NGFW',
		integration_type: 'Network Log',
	},
	{
		id: NXLOG_DEFINITION_ID,
		vendor: 'NXLog',
		product: 'NXLog Community Edition',
		version: '1.0.0',
		name: 'NX Log',
		description:
			'Windows Event Log and file-based logs, shipped to a Collector by an NXLog agent on the host.',
		event_types: [],
		visible: true,
		capabilities: ['Log Ingestion'],
		category: 'Log Collection / Forwarding',
		integration_type: 'Network Log',
	},
	{
		id: RSYSLOG_DEFINITION_ID,
		vendor: 'rsyslog',
		product: 'rsyslog',
		version: '1.0.0',
		name: 'rsyslog',
		description:
			'System and application logs from a Linux host, forwarded to a Collector by its rsyslog daemon.',
		event_types: [],
		visible: true,
		capabilities: ['Log Ingestion'],
		category: 'Log Collection / Forwarding',
		integration_type: 'Network Log',
	},
	{
		id: 'c2f8a41e-6b0d-4d27-9a3c-5e17b8d40f06',
		vendor: 'OISF',
		product: 'Suricata',
		version: '1.0.0',
		name: 'Suricata IDS',
		description:
			'Intrusion-detection alerts and flow records from a Suricata sensor, forwarded to a Collector over syslog.',
		event_types: [],
		visible: true,
		capabilities: ['Log Ingestion'],
		category: 'IDS / IPS',
		integration_type: 'Network Log',
	},
]

/**
 * The `LogSource.type` a source created from each definition would carry.
 *
 * The catalog names a *product* (`Palo Alto Firewall`) and a Log Source names a
 * *kind of feed* (`ngfw`) — the same two-name split the fixtures already use, so
 * something has to hold the pair. It lives here rather than beside the Log
 * Source fixtures because the left-hand side is a definition id, and this file
 * owns those.
 *
 * Read by the Add Log Source wizard's hand-off: finishing a `Palo Alto Firewall`
 * wizard should open a Palo Alto source, not whichever source happens to be
 * first on the Collector.
 */
const LOG_SOURCE_TYPE_BY_DEFINITION: Record<string, string> = {
	[PALO_ALTO_DEFINITION_ID]: 'ngfw',
	[FORTINET_DEFINITION_ID]: 'firewall',
	[CISCO_ASA_DEFINITION_ID]: 'firewall',
	[NXLOG_DEFINITION_ID]: 'nxlog',
	// rsyslog and Suricata have no fixture source of their own: the estate was
	// authored from the design's list frame, which shows neither. They fall back
	// to `null`, and the hand-off then ranks on the Collector alone.
}

/** The Log Source type a definition creates, or `null` when none is authored. */
export function logSourceTypeFor(definitionId: string): string | null {
	// `Object.hasOwn`, not `in` — a definition id of `constructor` would
	// otherwise walk the prototype chain.
	return Object.hasOwn(LOG_SOURCE_TYPE_BY_DEFINITION, definitionId)
		? LOG_SOURCE_TYPE_BY_DEFINITION[definitionId]
		: null
}

/**
 * **The seam.** Appends the seeded rows to whatever the catalog endpoint
 * returned, called once in `fetchAvailableIntegrations` — and not at all in the
 * demo build, which serves its own contract-authored catalog (ADR 0017).
 *
 * Appends rather than prepends so the Cloud tab — the default tab, and the only
 * one with real rows — keeps the server's order exactly as it is today. The rows
 * carry `integration_type: 'Network Log'`, so the add-catalog is the only surface
 * that changes *shape* (it is the only one that splits on that field) — but every
 * catalog consumer still sees them; see the file header.
 *
 * A real `Network Log` row from the server wins on id collision — it is filtered
 * out of the seed rather than rendered twice — so the day the backend seeds one
 * of these definitions, the tab shows the server's copy and not a stale twin.
 */
export function withNetworkLogCatalog(
	catalog: Array<AvailableIntegration>,
): Array<AvailableIntegration> {
	const serverIds = new Set(catalog.map((integration) => integration.id))

	return [
		...catalog,
		...NETWORK_LOG_CATALOG_FIXTURES.filter(({ id }) => !serverIds.has(id)),
	]
}
